import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Mail, Paperclip } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Soft } from "../../Common/Soft";
import { tokens } from "@/theme/tokens";
import { entryVariants, resolveTransition } from "../motion";
import type { PendingReview } from "../../../chat/hooks/useStreamingTurn";
import type { AttachmentPreview, RecipientVerdict } from "../../../chat/events";

export interface EmailApprovalCardProps {
  review: PendingReview;
  onDecide: (decision: "approve" | "reject") => void | Promise<void>;
}

/** How long the server keeps a pending approval before it disappears.
 *
 *  MIRRORS A SERVER DEFAULT THE CLIENT CANNOT READ: `approval_gate.py`'s
 *  `UNIFIED_AGENT_APPROVAL_TTL_S` (default 3600s). Nothing puts it on the wire,
 *  so this is a copy — an environment that overrides the env var makes this
 *  optimistic (we'd still offer a button for a call the server forgot) or
 *  pessimistic (we'd grey out a call still waiting). Both beat the alternative:
 *  spec §5.4 — the pending key TTLs and vanishes SILENTLY, with no sweep, no
 *  replay and no `stop_reason` on the `done` frame, so the server will never
 *  tell us. Without our own clock the card keeps a live-looking Send button
 *  whose click does nothing at all. */
export const APPROVAL_TTL_MS = 3600 * 1000;

/** Re-check the clock this often. Deliberately a poll against `Date.now()`
 *  rather than one long `setTimeout`: a laptop that sleeps through the hour is
 *  exactly the "stepped away" case §5.4 is about, and wall-clock arithmetic
 *  catches up on wake whether or not the timer fired on time. */
const EXPIRY_TICK_MS = 15_000;

/** How each resolver verdict reads to the person about to click Send (spec §3).
 *
 *  Tone is chosen per status and none of them is an error: `unrecognized_
 *  external` is a NOTICE, because external recipients are permitted by design
 *  and dressing one as a fault teaches people to click through warnings. Only
 *  `likely_typo` — a miss on the sender's OWN domain, the strongest signal we
 *  have that someone fat-fingered a colleague — gets the warning colour. */
const VERDICT: Record<RecipientVerdict["status"], { label: string; color: string }> = {
  // No name, only the address: `egpt_company_members` has no name column
  // (spec §3's recorded gap), so "show their name" cannot be honoured yet.
  known_member: { label: "In your organisation", color: "success.main" },
  likely_typo: { label: "Possible typo", color: "warning.main" },
  unrecognized_external: { label: "Outside your organisation", color: "info.main" },
  unrecognized: { label: "Not recognised", color: "text.secondary" },
};

/** Why the server would not name a file, in the words of the person about to
 *  click Send. Keyed on `EmailAttachmentResolver`'s refusal codes — the SAME
 *  codes the send itself raises, which is the point: the card is describing
 *  what the send will do, not guessing at it.
 *
 *  Never a filename. An entry that failed the ownership gate is someone
 *  else's private document and "salary-review.pdf" can be the entire
 *  disclosure, so the id is all the card has to identify it by. */
const ATTACHMENT_REFUSAL: Record<string, string> = {
  attachment_not_in_conversation: "is not part of this conversation",
  attachment_not_permitted: "is private to someone else",
  attachment_not_found: "is not available to you",
  attachment_lookup_failed: "could not be read",
  invalid_attachment_id: "is not a document id",
};

function refusalReason(error: string | undefined): string {
  // A code this build does not know still has to render as a refusal: the
  // server may add one, and silently showing nothing would leave the user
  // approving a send that cannot happen.
  return (error && ATTACHMENT_REFUSAL[error]) || "could not be identified";
}

/** The addresses this call will actually send to, from the args the gate held.
 *  The tool's schema is `string | string[]` and the model writes either. */
function addressesOf(args: Record<string, unknown> | null): string[] {
  const to = args?.to;
  const raw = Array.isArray(to) ? to : [to];
  return raw.filter((a): a is string => typeof a === "string" && a.trim() !== "");
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 0.75, alignItems: "baseline" }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", minWidth: 58, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Box>
  );
}

/**
 * The approval gate for a `send_email` call (spec §5.2). The turn ENDED on a
 * `review_required` frame and nothing runs until the user decides.
 *
 * Its one job is that **the card and the send agree**: every recipient, the
 * subject and the body exactly as they will go out.
 *
 * APPROVE AND REJECT ONLY — there is deliberately no edit affordance of any
 * kind, not even a clickable "did you mean" (spec §5.3). `effective_args`
 * takes `edited_args` wholesale with no schema check and `_approval_resume.py`
 * never compares the decision's `call_id` to the pending one, so an editable
 * card could display one email and send another. The only correction path is
 * reject and re-prompt. Do not add one back without fixing that first.
 *
 * Deciding parks a verdict AND opens the turn that applies it. /steer only
 * writes to `tool_decision:{session_id}`; `resume_pending_if_decided` consumes
 * it on turn entry and there is no resume endpoint, so `decideReview` opens
 * that turn itself (see useChatSession.openResumeTurn). The send therefore
 * happens on the click, which is what the button already implied.
 *
 * It did not always: this used to queue only, and the email left with the
 * user's NEXT message. The card said so, but a button that visibly does
 * nothing reads as broken however it is labelled.
 */
export function EmailApprovalCard({ review, onDecide }: EmailApprovalCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<"idle" | "approved" | "rejected">("idle");
  const [shownAt] = useState(() => Date.now());
  const [timedOut, setTimedOut] = useState(false);
  const reduce = useReducedMotion() ?? false;

  useEffect(() => {
    if (timedOut) return;
    const id = setInterval(() => {
      if (Date.now() - shownAt >= APPROVAL_TTL_MS) setTimedOut(true);
    }, EXPIRY_TICK_MS);
    return () => clearInterval(id);
  }, [timedOut, shownAt]);

  const args = review.args;
  const addresses = addressesOf(args);
  const subject = typeof args?.subject === "string" ? args.subject : "";
  const body = typeof args?.body === "string" ? args.body : "";
  // The server's per-file description, or ABSENT when it could not compute
  // one. Absent is not an error and not "no attachments" — it means nobody
  // checked, so the count from the raw args is all the card may claim.
  const described: AttachmentPreview[] | undefined = review.attachments;
  const attachmentCount = Array.isArray(args?.attachment_ids)
    ? args.attachment_ids.length
    : 0;
  // T10: one unresolvable id refuses the WHOLE send. That consequence belongs
  // ON the card — learning it after clicking is the failure this closes.
  const sendWillBeRefused = Boolean(described?.some((a) => !a.filename));
  // Matched by address rather than by position so a row can only ever carry
  // the verdict for the address printed beside it.
  const verdictFor = (address: string) =>
    review.recipients?.find((r) => r.email.toLowerCase() === address.toLowerCase());

  const expired = timedOut && outcome === "idle";
  // The gate failed CLOSED: `error` arrived instead of `args`, the tool never
  // ran, and a decision would be written somewhere nothing reads it. There is
  // nothing to decide, so don't offer a decision.
  const gateFailed = Boolean(review.error);
  const disabled = busy || expired || gateFailed;

  const decide = (decision: "approve" | "reject") => async () => {
    if (disabled) return;
    setBusy(true);
    setError(null);
    try {
      await onDecide(decision);
      setOutcome(decision === "approve" ? "approved" : "rejected");
    } catch (e) {
      // decideReview throws when the decision could never land. Swallowing it
      // would leave the user believing they approved a send that cannot happen.
      setError(e instanceof Error ? e.message : "Could not record your decision");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={entryVariants}
      transition={resolveTransition(reduce)}
    >
      <Soft
        variant="raised"
        role="region"
        aria-label="Email approval"
        sx={{
          my: 1.5,
          p: 1.5,
          boxShadow: (t) =>
            `${t.palette.mode === "light" ? tokens.neu.light.raised : tokens.neu.dark.raised}, inset 0 0 0 1px ${alpha(t.palette.warning.main, 0.45)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1, color: "warning.main" }}>
          <Mail size={16} aria-hidden />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Send this email?
          </Typography>
        </Box>

        {addresses.length > 0 && (
          <Field label="To">
            {addresses.map((address) => {
              const verdict = verdictFor(address);
              return (
                <Box key={address} sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: "text.primary", wordBreak: "break-all" }}>
                    {address}
                  </Typography>
                  {/* No verdict row at all when the deployment could not
                      resolve recipients (`recipients` absent). That is the
                      honest degraded state, not a failure: labelling an
                      address nobody checked would be a claim we never
                      established. */}
                  {verdict && (
                    <Typography variant="caption" sx={{ color: VERDICT[verdict.status].color }}>
                      {VERDICT[verdict.status].label}
                    </Typography>
                  )}
                  {/* INFORMATION ONLY — not a control. See the header comment:
                      with `edit` dropped there is no way to substitute it, so
                      making it clickable would promise something the gate
                      cannot honour. */}
                  {verdict?.suggestion && (
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                      {`Did you mean ${verdict.suggestion}?`}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Field>
        )}

        {subject && (
          <Field label="Subject">
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {subject}
            </Typography>
          </Field>
        )}

        {body && (
          <Field label="Message">
            {/* PLAIN TEXT, never markdown or HTML. The handler escapes the body
                before sending, so `<b>bold</b>` is delivered as those nine
                characters — rendering it as markup here would show the user
                something other than what leaves. `pre-wrap` keeps the model's
                own line breaks, which the send preserves too. */}
            <Typography
              data-testid="email-approval-body"
              variant="body2"
              component="div"
              sx={{
                color: "text.primary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 320,
                overflowY: "auto",
              }}
            >
              {body}
            </Typography>
          </Field>
        )}

        {described && described.length > 0 && (
          <Field label="Files">
            {described.map((attachment) => (
              <Box
                key={attachment.document_id}
                data-testid="email-approval-attachment"
                sx={{ display: "flex", gap: 0.75, alignItems: "baseline", mb: 0.25 }}
              >
                <Paperclip size={12} aria-hidden style={{ flexShrink: 0 }} />
                {attachment.filename ? (
                  <Typography
                    variant="body2"
                    sx={{ color: "text.primary", wordBreak: "break-all" }}
                  >
                    {attachment.filename}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: "warning.main" }}>
                    {`Document ${attachment.document_id} ${refusalReason(attachment.error)}`}
                  </Typography>
                )}
              </Box>
            ))}
          </Field>
        )}

        {sendWillBeRefused && (
          <Typography variant="body2" sx={{ color: "warning.main", mb: 1 }}>
            {/* `send_email` attaches every file or none (T10), so ONE entry it
                cannot identify dooms the whole send. Said here rather than
                disabling Send: the decision is still the user's, and the
                server — not this card — is the thing that refuses. */}
            One of these files cannot be attached, so this send will be refused
            and nothing will be delivered. Don&apos;t send it — ask again
            without that file.
          </Typography>
        )}

        {!described && attachmentCount > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
            {/* Count only, as before: the gate could not describe the files, so
                the payload carries opaque document ids and we do not invent a
                name — or a verdict — for any of them. */}
            {`${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"} from this conversation will be included (names not shown).`}
          </Typography>
        )}

        {/* Nothing email-shaped to show (the gate failed before it captured the
            arguments) — fall back to the server's own rendering of the call
            rather than an empty card. */}
        {addresses.length === 0 && !subject && !body && review.summary && (
          <Typography variant="body2" sx={{ color: "text.primary", mb: 0.5 }}>
            {review.summary}
          </Typography>
        )}

        {gateFailed && (
          <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
            {`This approval could not be set up (${review.error}). Nothing was sent — ask for the email again.`}
          </Typography>
        )}

        {expired && (
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            This approval expired and the server has already discarded it. Nothing was
            sent — ask for the email again if you still want it.
          </Typography>
        )}

        {outcome === "idle" ? (
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button size="small" variant="contained" disabled={disabled} onClick={decide("approve")}>
              Send email
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={disabled}
              onClick={decide("reject")}
            >
              Don&apos;t send
            </Button>
          </Box>
        ) : (
          <Typography
            variant="caption"
            sx={{ color: outcome === "approved" ? "success.main" : "text.secondary", mt: 1, display: "block" }}
          >
            {outcome === "approved" ? "Approved — sending…" : "Not sent."}
          </Typography>
        )}

        {error && (
          <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Soft>
    </motion.div>
  );
}
